/**
 * Extracts an array of primitive values from a specific field in an array of objects.
 *
 * @example
 * | Array                      | Selected Field | Operation                          | Result       |
 * |----------------------------|---------------|-------------------------------------|--------------|
 * | `[{                        | `"name"`      | `extractPrimitiveValuesByFields    `| `[           |
 * |   id: 1,                   |               |                                     |   "Alice",   |
 * |   name: "Alice"            |               |                                     |   "Bob"      |
 * | }]`                        |               |                                     | ]`           |
 */
export function extractPrimitiveValuesByFieldAsArray<T, K extends keyof T>(
  arr: T[],
  field: K
): Array<T[K]> {
  return arr.map((item) => item[field]);
}

/**
 * Finds and returns the first object in the array where the given field matches the specified value.
 *
 * @example
 * | Array                      | Field  | Value | Operation                | Result                |
 * |----------------------------|--------|-------|------------------------  |----------------------|
 * | `[{                        | `"id"` | `2`   | `findFirstObjectByField` | `{                   |
 * |   id: 1,                   |        |       |                          |   id: 2,             |
 * |   name: "Alice"            |        |       |                          |   name: "Bob"        |
 * | }]`                        |        |       |                          | }`                   |
 */
export function findFirstObjectByField<T, K extends keyof T>(
  arr: T[],
  field: K,
  value: T[K]
): T | undefined {
  return arr.find((item) => item[field] === value);
}

/**
 * Returns an array of primitive values that exist in the given list and also exist in the objects array.
 *
 *  * @example
 * | Array                      | Field  | Values    | Operation                       | Result    |
 * |----------------------------|--------|-----------|-------------------------------- |-----------|
 * | `[{                        | `"id"` | `[1, 3]`  | `filterMatchingPrimitiveValues` | `[        |
 * |   id: 1,                   |        |           |                                 |   1       |
 * |   name: "Alice"            |        |           |                                 | ]`        |
 * | }]`                        |        |           |                                 |           |
 */
export function filterMatchingPrimitiveValues<T, K extends keyof T>(
  arr: T[],
  field: K,
  values: Array<T[K]>
): Array<T[K]> {
  return values.filter((value) => arr.some((item) => item[field] === value));
}

/**
 * Filters and returns an array of objects whose field values match any value from the provided list.
 *
 * @template T - Type of objects in the array.
 * @template K - Keys of T to match values against.
 *
 * @param arr - The array of objects to filter.
 * @param field - The object key whose value is used for filtering.
 * @param values - An array of values to match against the specified field.
 * @param returnEmptyIfValuesEmpty - If true, returns an empty array when `values` is null or empty. Defaults to false.
 *
 * @returns A filtered array containing only the objects whose field matches any of the provided values.
 *
 * @example
 * filterArrayOfObjectsByFieldValues(
 *   [{ id: 1, name: 'Alice' }, { id: 2, name: 'Bob' }, { id: 3, name: 'Charlie' }],
 *   'name',
 *   ['Alice', 'Charlie']
 * );
 * // Returns: [{ id: 1, name: 'Alice' }, { id: 3, name: 'Charlie' }]
 *
 * @example
 * filterArrayOfObjectsByFieldValues(
 *   [{ id: 1, name: 'Alice' }],
 *   'name',
 *   [],
 *   true
 * );
 * // Returns: []
 */
export function filterArrayOfObjectsByFieldValues<T, K extends keyof T>(
  arr: T[],
  field: K,
  values: Array<T[K]>,
  returnEmptyIfValuesEmpty = false
): T[] {
  if (!values || values.length === 0) {
    return returnEmptyIfValuesEmpty ? [] : arr;
  } else if (arr && arr.length > 0) {
    return arr.filter((item) => values.includes(item[field]));
  }
  return [];
}

/**
 * Filters an array of objects based on a search term across multiple fields.
 *
 * @param arr - Array of objects (templates)
 * @param searchTerm - Text to search
 * @param fields - Keys of the object to check against
 * @returns Filtered array of objects
 *
 *  @example
 * const templates = [
 *   { id: 1, name: 'Review Request', subject: 'Do you have a moment?' },
 *   { id: 2, name: 'Follow Up', subject: 'Thank you for visiting!' },
 *   { id: 3, name: 'Promotion', subject: 'Special offer for you' }
 * ];
 *
 * // Search by name or subject
 * filterBySearchFields(templates, 'review', ['name', 'subject']);
 * // Returns: [{ id: 1, name: 'Review Request', subject: 'Do you have a moment?' }]
 *
 * // Empty search term returns all
 * filterBySearchFields(templates, '', ['name', 'subject']);
 * // Returns all 3 templates
 */
export function filterBySearchFields<T extends Record<string, any>>(
  arr: T[],
  searchTerm: string,
  fields: Array<keyof T>
): T[] {
  if (!searchTerm?.trim()) {
    return arr;
  }

  return arr.filter((item) =>
    fields.some(
      (field) =>
        item[field] &&
        String(item[field]).toLowerCase().includes(searchTerm.toLowerCase())
    )
  );
}

/**
 * Filters a primitive array to only include specified values
 * @param array - The source array of primitives (string[], number[], etc.)
 * @param values - The values to filter by
 * @param returnEmptyIfValuesEmpty - If true, returns empty array when values is empty
 * @returns Filtered array
 */
export function filterPrimitiveArray<T>(
  array: T[],
  values: T[],
  returnEmptyIfValuesEmpty = true
): T[] {
  // Return empty array if source array is null/undefined
  if (!array || !Array.isArray(array)) {
    return [];
  }

  // Return empty or original based on flag
  if (!values || values.length === 0) {
    return returnEmptyIfValuesEmpty ? [] : array;
  }

  // Filter array to only include values that exist in the values array
  return array.filter((item) => values.includes(item));
}

/**
 * Returns an array of matching IDs from an options list based on selected IDs.
 *
 * 🔹 Use case:
 * - When you have a list of objects (e.g., dropdown options)
 * - And a list of selected primitive IDs
 * - This ensures only valid IDs (present in options) are returned
 *
 * @template T - Type of objects in options array
 *
 * @param options - Array of objects (e.g., [{ id, name }])
 * @param selectedIds - Array of selected IDs (e.g., [112157, 116924])
 * @param key - Object key to match against (usually 'id' or 'value')
 *
 * @returns Array of valid matching IDs
 *
 * @example
 * const result = getValidSelectedIds(options, [1, 3], 'id');
 * // returns: [1, 3] (only if they exist in options)
 */
export function getValidSelectedIds<T>(
  options: T[],
  selectedIds: any[],
  key: keyof T
): any[] {
  if (!options?.length || !selectedIds?.length) {
    return [];
  }

  return options
    .filter((item) => selectedIds.includes(item[key])) // keep only matching items
    .map((item) => item[key]); // return only IDs
}
